import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Collapse,
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Checkbox,
  Skeleton,
  FormControlLabel,
  FormGroup,
  Select
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  DeleteOutline,
  Search,
  DownloadOutlined,
  UploadOutlined
} from '@mui/icons-material';
import MobiledataOffIcon from '@mui/icons-material/MobiledataOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import { styled } from '@mui/system';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { ReactComponent as TrendSymbol } from '../../assests/images/Trend.svg'; // Make sure the path is correct
import Popover from '@mui/material/Popover';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DetailTable from './DetailTable';
import CustomAutoComplete from '../CustomAutoComplete/CustomAutoComplete';
import CellContent from './CellContent';
import ReviewItemsCellContent from './ReviewItemsCellContent';
import { useOkta } from '../../hooks/oktaHook';
import { triggerNotification } from '../../utils/NotificationContext';
import AdvancedFilter from '../../components/Modals/AdvanceFilter';
import { generateFilterOptions, hasCommonElement } from '../../utils/Utils';
import refreshIcon from '../../assests/images/refresh.svg';
import { fetchMaterialDetailByTPOSKU } from '../../pages/PortfolioMasterData/services';
import { DrawerColumns } from './utils';
import {
  CORE_USER,
  GENERAL_USER,
  TPO_ADMIN,
  FINANCIAL_KPI_COLUMNS,
  KPI_HIGHLIGHTER
} from '../../utils/Constants';
import PopDrawer from './Drawer';
import { Pagination } from 'pepsico-ds';
import SKUModal from '../Modals/SKUModal';
import { fetchProductAttributes } from '../../pages/FeedbackCollation/servies';
import PopCommentDrawer from './SKUComment';
import { setReviewItemsTableData } from '../../redux/PortfolioMaster/portfolioMasterSlice';
import NumberRangePopup from './Popup';
import { generateKPIHighlightText } from '../../pages/FeedbackCollation/utils';
import DMCPopupTable from '../../pages/DecisionMakingCentral/DecisionTablePopup';

// Define custom styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  position: 'relative',
  maxHeight: 'calc(100vh - 210px)',
  overflowY: 'auto'
}));

const StyledTableRow = styled(TableRow)(({ theme, isExpanded, isRowSelected }) => ({
  backgroundColor: isExpanded || isRowSelected ? '#DFEAF9' : '',
  borderBottom: isExpanded ? '1.5px solid #DFDFDF' : '1.5px solid #DFEAF9'
}));

const StyledTableCell = styled(TableCell)(
  ({ theme, sticky, left, right, showBorder, width, isCheckbox, isRecommendationTable }) => ({
    // padding: "16px",
    borderBottom: 'none',
    color: '#616161',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    zIndex: sticky ? 1 : 'auto',
    width: sticky ? width : 'auto',
    minWidth: sticky ? width : 'auto',
    position: sticky ? 'sticky' : 'relative',
    backgroundColor: sticky && !showBorder && !isCheckbox ? '#EFF5FC' : sticky ? '#fff' : 'inherit',
    left: sticky ? left : 'auto',
    right: sticky ? right : 'auto',
    height: isRecommendationTable ? '52px' : 'auto',
    '&:after':
      showBorder && sticky
        ? {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '3px', // or adjust as needed
            backgroundColor: theme.palette.divider,
            zIndex: 2 // Ensure it appears above other content
          }
        : {}
  })
);

const StyledTableHeadCell = styled(StyledTableCell)(({
  theme,
  sticky,
  left,
  right,
  showBorder,
  width,
  isCheckbox,
  isRecommendationTable,
  customerFilter,
  customerKey
}) => {
  return {
    fontSize: '12px',
    fontWeight: 700,
    zIndex: sticky ? 3 : 2,
    width: sticky ? width : 'auto',
    minWidth: sticky ? width : 'auto',
    position: 'sticky',
    top: 0,
    backgroundColor:
      sticky && !showBorder && !isCheckbox
        ? '#EFF5FC'
        : sticky
          ? '#fbfcfe'
          : customerFilter?.length > 0 &&
              KPI_HIGHLIGHTER?.level_attribute_name?.columns?.includes(customerKey)
            ? KPI_HIGHLIGHTER?.level_attribute_name?.color
            : '#fff',
    left: sticky ? left : 'auto',
    right: sticky ? right : 'auto',
    height: isRecommendationTable ? '42px' : 'auto',
    '&:after':
      showBorder && sticky
        ? {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '3px', // or adjust as needed
            backgroundColor: theme.palette.divider,
            zIndex: 2 // Ensure it appears above other content
          }
        : {}
  };
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: '#A0A0A0', // Color for input text
    paddingLeft: '8px', // Add padding to the left
    paddingRight: '8px',
    fontSize: '14px'
  },
  '& .MuiInputBase-input': {
    color: '#616161'
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: '#C0C0C0'
  },
  '& .MuiInput-underline:hover:before': {
    borderBottomColor: '#C0C0C0'
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#C0C0C0'
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#A0A0A0'
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    fontSize: '18px', // Smaller size for the search icon
    color: '#000000' // Black color for the search icon
  }
}));

const StyledPagination = styled(Pagination)(({ theme }) => ({
  '& .MuiPaginationItem-root': {
    color: '#005CBC',
    backgroundColor: '#EFF5FC',
    border: '1px solid #D7E5F8',
    '&.Mui-selected': {
      backgroundColor: '#005CBC',
      color: '#fff',
      fontWeight: 700
    },
    '&:hover': {
      'not(.Mui-selected)': {
        backgroundColor: '#DFEAF9'
      }
    }
  },
  '& .MuiPaginationItem-ellipsis': {
    color: '#616161'
  }
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: '#005CBC',
  width: '24px',
  height: '24px'
}));

function DynamicTable({
  originalColumns = [],
  originalData = [],
  tableName,
  isTableLoading,
  isSearch = true, // kept true for showing search on all grid
  isColumnVisibleFilter = false,
  isColumnFilter = false,
  isCollapseButton = true,
  isBulkUpload = false,
  isUploadDownloadDisabled = false,
  downloadExcel = false,
  isGlobalFilter = false,
  isAddRow = false,
  isEditable = false,
  density = 'medium',
  yearMonthData,
  onSave,
  onUpdate,
  onEdit,
  handleDownloadData,
  onBulkUploadModalOpen,
  isLeadsList = [],
  isRefresh = false,
  showOverLayTable = false,
  showTableText = false,
  isRowChecked = false,
  checkedRows,
  customerFilter,
  editedFCRows,
  setCheckedRows,
  getCycleHistory,
  currentVotingTeam,
  showCheckbox = false,
  setIsCycleEdit,
  isCycleEdit,
  editingRowId,
  setEditingRowId,
  setShowCycleSave,
  showCycleSave,
  handlePassRowPerPage = () => {}, //! additional props for chunking,
  handleLogsData,
  isSteeringRecoCompleted,
  handleGoalsModal,
  showCycleDrawer,
  handleModalAction,
  defaultRowsPerPage = 20,
  filterPagination
}) {
  const { userDetails } = useOkta();
  const dispatch = useDispatch();
  const tableFiltersRef = useRef();
  const tableContainerRef = useRef();
  //  set row per page =10 only for master data table
  const initialRowsPerPage = ['Master Data Table', 'Update Log', 'SKU Master'].includes(tableName)
    ? 10
    : defaultRowsPerPage;

  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [columns, setColumns] = useState(originalColumns);
  const [data, setData] = useState(originalData);
  const [rowChildData, setRowChildData] = useState([]);
  const [changedData, setChangedData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });
  const [page, setPage] = useState(0);
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const [isNewRowVisible, setIsNewRowVisible] = useState(false);
  const [newRowData, setNewRowData] = useState({});
  const [editCellData, setEditCellData] = useState([]);
  const [renderFlag, setRenderFlag] = useState(false);
  const [attributeChecked, setAttributeChecked] = useState(false);
  const [financeChecked, setFinanceChecked] = useState(false);
  const [isAdvanceFilterModalOpen, setIsAdvanceFilterModalOpen] = useState(false);
  const [showSKUCommentDrawer, setShowSKUCommentDrawer] = useState(false);
  const [showCommentDrawer, setShowCommentDrawer] = useState(false);
  const [rowData, setRowData] = useState({}); // comment row data
  const [rowIndex, setRowIndex] = useState(0); //row number detection
  const [skuData, setSKUData] = useState('');
  const [commentType, setCommentType] = useState('');
  const [selectedRangeFilters, setSelectedRangeFilters] = useState({}); // Track active range filters
  // SKU Modal state
  const [openSKUModal, setOpenSKUModal] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedColumnRangeFilter, setSelectedColumnRangeFilter] = useState(null);
  const [productAttr, setProductAttr] = useState({});

  const decisionCentralRules = useSelector((state) => state.decisionMakingCentralRules);
  const reviewItemsTableData = useSelector((state) => state?.portfolioMaster?.reviewItemsTableData);
  const { editedRows = [] } = decisionCentralRules;
  const { search } = useLocation();

  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const [numberRangePopupOpen, setNumberRangePopupOpen] = useState(false);
  const [selectedColumnLabel, setSelectedColumnLabel] = useState('');
  const [DMCRegionPopup, setDMCPopupOpen] = useState(false);
  const [DMCRegionCol, setDMCRegionCol] = useState({});
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(e.target.value);
    if (tableName === 'Recommendation Table') {
      handlePassRowPerPage(e.target.value); //! passing to parent the selected row per page size for chunking
    }
    setPage(0);
  };
  const handleDrawerChange = (value, row, column, index) => {
    setShowCommentDrawer(true);
    setRowData(row);
    setRowIndex(index);
  };

  const handleDrawerCommentChange = (value, type, row, column, index) => {
    setShowSKUCommentDrawer(true);
    setRowData(row);
    setRowIndex(index);
    setCommentType(type);
    setOpenSKUModal(false);
  };

  const toggleDrawerHandler = (val) => {
    setShowCommentDrawer(val);
  };

  const toggleDrawerCommentHandler = (val, data) => {
    setShowSKUCommentDrawer(val);
    setOpenSKUModal(true);
    setRowData(data);
  };

  // drawer option
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const isRecommendationTable =
    tableName === 'Recommendation Table' ||
    tableName === 'Decision Table' ||
    tableName === 'Completion Table';

  const drawerCollapse = () => {
    if (!drawerOpen) {
      const stickyColumns = originalColumns?.filter((item) => item.sticky);
      stickyColumns?.shift();
      setColumns([...columns, ...stickyColumns]);
    } else {
      const tpoSkuColumn = [columns?.[0]];
      const filteredColumns = columns?.filter((item) => !item.sticky);
      setColumns([...tpoSkuColumn, ...filteredColumns]);
    }
    setDrawerOpen(!drawerOpen);
  };

  const [creteCycleValue, setCreteCycleValue] = useState(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const sortKeys = {
    'Master Data Table': 'CurrentYearRevenue',
    'Recommendation Table': 'revenue',
    'SKU Master': 'NetRevenue',
  };
  const open = Boolean(anchorEl);
  //Setting default sorting to revenue
  useEffect(() => {
    setSortConfig(
      sortKeys[tableName]
        ? { key: sortKeys[tableName], direction: 'dsc' }
        : { key: null, direction: null }
    );
  }, []);
  // Calling custom hook here to check permission level
  useEffect(() => {
    if (tableName == 'Master Data Table' || tableName == 'SKU Master') {
      const attributeColumns = originalColumns?.filter(
        (col) => col?.mappingCategory == 'attributes' || col?.mappingCategory == 'attributesfinance'
      );
      const financeColumns = originalColumns?.filter(
        (col) => col?.mappingCategory == 'finance' || col?.mappingCategory == 'attributesfinance'
      );
      const attributeFinanceColumns = [
        ...attributeColumns,
        ...financeColumns.filter((colB) => !attributeColumns.some((colA) => colA.id === colB.id))
      ];

      if (attributeChecked && financeChecked) {
        setColumns(attributeFinanceColumns);
      } else if (attributeChecked) {
        setColumns(attributeColumns);
      } else if (financeChecked) {
        setColumns(financeColumns);
      } else {
        setColumns(originalColumns);
      }
    } else {
      setColumns(originalColumns);
    }
  }, [originalColumns, originalColumns.length, attributeChecked, financeChecked]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page on new search
  };
  const handleCheckboxChange = (e, row) => {
    let filteredRows = [...checkedRows];
    if (e.target.checked) {
      filteredRows.push(row.CycleSKUKey);
    } else {
      filteredRows = checkedRows?.filter((key) => key !== row.CycleSKUKey);
    }
    setCheckedRows([...new Set(filteredRows)]);
  };

  const handleCategoryMappingChange = (e) => {
    const { name, checked } = e.target;

    if (name === 'attributes') {
      setAttributeChecked(checked);
    } else if (name === 'finance') {
      setFinanceChecked(checked);
    }
  };

  const handleAllRowsChecked = (e) => {
    if (e.target.checked) {
      setCheckedRows(currentPageData?.map((item) => item.CycleSKUKey));
    } else {
      setCheckedRows([]);
    }
  };

  const handleRequestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
    setPage(0);
  };

  const filteredData = (
    filters //! to check
  ) =>
    data.filter((row) => {
      for (const column in filters) {
        // eslint-disable-next-line no-prototype-builtins
        if (filters.hasOwnProperty(column)) {
          const columnFilter = filters[column];
          const isAnyFilterSelected =
            columnFilter.some((checked) => checked) ||
            columnFilter?.includes('') ||
            columnFilter?.includes(0);
          const filtersToCheck = ['agree', 'disagree'];
          if (isRecommendationTable && isAnyFilterSelected) {
            if (hasCommonElement(filtersToCheck, columnFilter)) {
              if (columnFilter?.includes('agree') && row[column] === true) {
                return true;
              } else if (columnFilter?.includes('disagree') && row[column] === false) {
                return true;
              } else {
                return false;
              }
            } else if (Object.keys(filters)?.[0] === 'taggedUsers') {
              return row.taggedUsers?.filter((item) =>
                filters['taggedUsers']?.includes(item.UserName)
              )?.length > 0
                ? true
                : false;
            } else if (column === 'votes aligned') {
              if (columnFilter?.includes('Yes') && row['votes aligned']) {
                return true;
              } else if (columnFilter?.includes('No') && row['votes aligned'] === false) {
                return true;
              } else {
                return false;
              }
            }
          }
          if (isAnyFilterSelected && !columnFilter?.includes(row[column])) {
            return false;
          }
        }
      }
      for (const column in selectedRangeFilters) {
        // eslint-disable-next-line no-prototype-builtins
        if (selectedRangeFilters?.hasOwnProperty(column)) {
          const { selectedRange } = selectedRangeFilters[column];
          if (selectedRange.length === 2) {
            const value = row[column];
            if (value < selectedRange[0] || value > selectedRange[1]) {
              return false;
            }
          }
        }
      }
      return true;
    });

  const handleExpandClick = async (row, rowIndex) => {
    if (tableName == 'Master Data Table') {
      const materialDetails = await fetchMaterialDetailByTPOSKU(row.TPOSKU, row.BusinessUnitName);
      const selectedMaterialDetails = materialDetails.map(
        ({
          UDCJDACode,
          MaterialID,
          MaterialName,
          ProductHierarchyLevel1Description,
          ProductHierarchyLevel2Description,
          GrossSales,
          GSvsLY,
          OnTicketTradeSpend,
          NetInvoiceSales,
          NSvsLY,
          OffTicketTradeSpend,
          NetRevenue,
          COGS,
          GrossProfit,
          GPPercent,
          TotalSD,
          NetProfit,
          NPvsLY,
          CasesvsLY,
          WeightvsLY,
          TotalWaste,
          FacilityCountL12W,
          NetVolume
        }) => ({
          MaterialName,
          MaterialID,
          UDCJDACode,
          ProductHierarchyLevel1Description,
          ProductHierarchyLevel2Description,
          GrossSales,
          GSvsLY,
          OnTicketTradeSpend,
          NetInvoiceSales,
          NSvsLY,
          OffTicketTradeSpend,
          NetRevenue,
          COGS,
          GrossProfit,
          GPPercent,
          TotalSD,
          NetProfit,
          NPvsLY,
          CasesvsLY,
          WeightvsLY,
          TotalWaste,
          FacilityCountL12W,
          NetVolume
        })
      );
      setRowChildData(selectedMaterialDetails);
    }
    if (tableName == 'Cycles List') {
      const cycleHistoryDetails = await getCycleHistory(row.id);
      const selectedHistoryDetails = cycleHistoryDetails.map(
        ({ historyTime, status, submittedBy }) => ({
          'HISTORY TIME': new Date(historyTime * 1000)
            .toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
              timeZoneName: 'shortOffset' // Shows local GMT offset automatically
            })
            .replace(',', ''),
          'SUBMITTED BY': submittedBy,
          status
        })
      );
      setRowChildData(selectedHistoryDetails);
    }

    setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
  };
  useEffect(() => {
    if (skuData && selectedCycleId) {
      getProductAttr(skuData, selectedCycleId);
    }
  }, [rowData?.tposku, rowIndex]);

  const getProductAttr = async (skuData, selectedCycleId) => {
    try {
      const content = await fetchProductAttributes(skuData, selectedCycleId);
      if (content) {
        setProductAttr(content);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleValueChange = (value, row, column) => {
    const updatedData = data.map((item) => {
      if (item.id === row.id) {
        return column.id === 'threshold' ? { ...item, ...value } : { ...item, [column.id]: value };
      }
      return item;
    });

    setData(updatedData);

    const updatedChangedData = [...changedData];
    const existingRowIndex = updatedChangedData.findIndex((data) => data.id === row.id);

    if (existingRowIndex !== -1) {
      updatedChangedData[existingRowIndex] = {
        ...updatedChangedData[existingRowIndex],
        [column.id]: value
      };
    } else {
      updatedChangedData.push({
        id: row.id,
        [column.id]: value
      });
    }
    setChangedData(updatedChangedData);
  };
  const handleRadioChange = (value, row, column) => {
    const updatedData = data.map((item) => {
      if (item.activeStatus) {
        return { ...item, [column.id]: false };
      }
      if (item.id === row.id) {
        return { ...item, [column.id]: value };
      }

      return item;
    });

    setData(updatedData);
  };

  const handleDiscardChanges = () => {
    if (tableName == 'Review Items Table') {
      dispatch(setReviewItemsTableData(originalData));
    }
    setData(originalData);
    setChangedData([]);
    setNewRowData({});
  };
  const handleCycleDiscardChanges = () => {
    if (tableName == 'Review Items Table') {
      dispatch(setReviewItemsTableData(originalData));
    }
    setData(originalData);
    setChangedData([]);
    setNewRowData({});
    setEditingRowId(null);
    setIsCycleEdit(false);
  };
  const handleCheckItemDiscardChanges = () => {
    setRenderFlag(!renderFlag);
    if (tableName == 'Review Items Table') {
      dispatch(setReviewItemsTableData(originalData));
    }
    setData(originalData);
    setEditCellData([]);
    setChangedData([]);
    setNewRowData({});
  };

  const handleSaveChanges = () => {
    if (Object.keys(newRowData).length) {
      onSave(newRowData);
    }
    if (Object.keys(changedData).length) {
      onUpdate(changedData);
    }
    setChangedData([]);
    setNewRowData({});
    setIsNewRowVisible(false);
  };

  const handleCheckItemSaveChanges = () => {
    if (Object.keys(editCellData).length) {
      onSave(editCellData);
    }
    setEditCellData([]);
    setChangedData([]);
    setNewRowData({});
    setIsNewRowVisible(false);
  };

  const handleCycleCreate = () => {
    if (isCycleEdit) {
      if (Object.keys(changedData).length) {
        onEdit(changedData, editingRowId, originalData);
      }
    } else {
      if (Object.keys(newRowData).length) {
        if (!newRowData.cycleName) {
          triggerNotification('Cycle Name Cannot be Blank', 'error');
          return;
        }
        if (!newRowData.startYearPeriod) {
          triggerNotification('Please Select Start Period', 'error');
          return;
        }
        if (!newRowData.endYearPeriod) {
          triggerNotification('Please Select End Period', 'error');
          return;
        }

        onSave(newRowData, creteCycleValue);
      }
      if (Object.keys(changedData).length) {
        onUpdate(changedData);
      }
      setChangedData([]);
      setNewRowData({});
      setIsNewRowVisible(false);
      setCreteCycleValue(null);
      setShowCycleSave(true);
    }
  };

  const handleDeleteNewRow = () => {
    setNewRowData({});
    setIsNewRowVisible(false);
  };

  const handleNewRowChange = (e) => {
    const { name, type, value, checked } = e.target;

    setNewRowData({
      ...newRowData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleEditCell = (rowId, columnId, content, row = {}) => {
    // if (columnId == 'DiscontinuedPeriodValue' || columnId == 'LaunchPeriodValue') {
     if (columnId == 'DiscontinuedPeriodValue') {
      const updatedData = reviewItemsTableData.map((item) => {
        if (item.TPOSKU === rowId) {
          return { ...item, [columnId]: content };
        }

        return item;
      });
      dispatch(setReviewItemsTableData(updatedData));
    }
    setEditCellData((prevArray) => {
      const updatedArray = prevArray.map((item) =>
        item.TPOSKU === rowId ? { ...item, [columnId]: content } : item
      );
      // If the item with the given rowId was not found, add a new object
      if (!prevArray.some((item) => item.TPOSKU === rowId)) {
        return [
          ...updatedArray,
          {
            TPOSKU: rowId,
            [columnId]: content,
            LastUpdatedEmailIDWebapp: userDetails?.email,
            BuTpoSkuKey: row?.BuTpoSkuKey
          }
        ];
      }

      return updatedArray;
    });
  };

  const handleChangePage = (newPage) => {
    if (tableName == 'Recommendation Table') {
      if (editedFCRows && editedFCRows?.length > 0) {
        triggerNotification('Your changes have been auto-saved', 'success');
      }
    } else if (tableName == 'Completion Table' || tableName == 'Decision Table') {
      if (editedRows && editedRows?.length > 0) {
        triggerNotification('Your changes have been auto-saved', 'success');
      }
    }

    setPage(newPage - 1);
  };

  const handleColumnVisibilityChange = (columnList) => {
    let filterSelectedColumns = [];
    if (isRecommendationTable) {
      const stickyColumns = originalColumns.filter((column) => column.sticky);
      let filteredColumns = [stickyColumns[0]];
      filteredColumns = [
        ...filteredColumns,
        ...originalColumns
          .filter((item, index) => !item.sticky)
          .filter((column) => columnList.includes(column.label))
      ];
      stickyColumns?.shift();
      filterSelectedColumns = drawerOpen
        ? [...filteredColumns, ...stickyColumns]
        : [...filteredColumns];
    } else {
      filterSelectedColumns = originalColumns.filter((column) => columnList.includes(column.label));
    }
    setColumns(filterSelectedColumns);
  };

  const handleFilterChange = (columnId, selectedValues) => {
    const updatedFilters = {
      ...selectedFilters,
      [columnId]: selectedValues
    };
    const regionalAlgoColumns = columns
      ?.map((list) => list.id)
      ?.map((item) => {
        if (item?.includes('agree steering team reco')) {
          return item;
        }
      })
      ?.filter((li) => li !== undefined);
    regionalAlgoColumns.push('steering team agreement');
    // Filter table data
    const newFilteredData = filteredData(updatedFilters);
    // update filter options
    const filters = originalColumns.reduce((acc, col) => {
      acc[col.id] =
        col.id !== columnId
          ? [
              ...new Set(
                newFilteredData.map((row) => {
                  return Array.isArray(row[col.id])
                    ? row[col.id]?.[0]?.UserName
                    : typeof row[col.id] === 'boolean'
                      ? row[col.id]
                        ? 'agree'
                        : 'disagree'
                      : row[col.id];
                })
              )
            ]
          : regionalAlgoColumns?.includes(columnId)
            ? [
                ...(filterOptions[columnId] = [
                  ...new Set(
                    newFilteredData?.map((row) => {
                      if (col.id === columnId) {
                        return row[col.id] ? 'agree' : 'disagree';
                      }
                    })
                  )
                ])
              ]
            : [...filterOptions[col.id]]; // Get unique values
      return acc;
    }, {});
    setFilterOptions(filters);
    setSelectedFilters(updatedFilters);
    setPage(0); // Reset to first page on new search
  };

  const handleApplyAdvanceFilter = (selectedFilters) => {
    setSelectedFilters(selectedFilters);
    setPage(0);

    // update options
    const newFilteredData = filteredData(selectedFilters);
    const newFilterOptions = generateFilterOptions(
      originalColumns,
      newFilteredData,
      isRecommendationTable
    );
    setFilterOptions(newFilterOptions);
  };
  const handleResetFilters = () => {
    setSelectedFilters({});
    setSelectedRangeFilters({});
    setSearchQuery('');
    setPage(0);
    setData(originalData);
    setSortConfig(
      sortKeys[tableName]
        ? { key: sortKeys[tableName], direction: 'dsc' }
        : { key: null, direction: null }
    );
    const newFilterOptions = generateFilterOptions(
      originalColumns,
      originalData,
      isRecommendationTable
    );
    setFilterOptions(newFilterOptions);
  };
  const searchedData = filteredData(selectedFilters)?.filter((row) => {
    return columns.some((column) =>
      row[column.id]?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const sortedData = [...searchedData].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (key && direction) {
      let columnA = a[key] ?? '';
      let columnB = b[key] ?? '';

      // Handle numeric values
      if(!isNaN(columnA) && !isNaN(columnB)) {
      // Convert to numbers for numeric comparison
        // Treat blank ("") as 0
        const numA = columnA === "" ? 0 : Number(columnA);
        const numB = columnB === "" ? 0 : Number(columnB);
        if (direction === "asc") {
          // negatives first
          if (numA < 0 && numB >= 0) return -1;
          if (numA >= 0 && numB < 0) return 1;

          // zeros before positives
          if (numA === 0 && numB > 0) return -1;
          if (numB === 0 && numA > 0) return 1;

          return numA - numB; // natural order within same category
        } else {
          // positives first
          if (numA > 0 && numB <= 0) return -1;
          if (numA <= 0 && numB > 0) return 1;

          // zeros before negatives
          if (numA === 0 && numB < 0) return -1;
          if (numB === 0 && numA < 0) return 1;

          return numB - numA; // natural order within same category
        }
      }

      // Handle string values
      columnA = String(columnA).toLowerCase();
      columnB = String(columnB).toLowerCase();

      // Handle blank string values
      if (columnA === '' && columnB !== '') {
        return direction === 'asc' ? 1 : -1;
      }
      if (columnB === '' && columnA !== '') {
        return direction === 'asc' ? -1 : 1;
      }
      if (columnA === '' && columnB === '') {
        return 0;
      }

      // Normal string comparison
      if (columnA < columnB) {
        return direction === 'asc' ? -1 : 1;
      }
      if (columnA > columnB) {
        return direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  const handleInfoClick = (column, row, index) => {
    setSelectedColumn(column);
    setRowData(row);
    setRowIndex(index);
    setSKUData(row?.tposku);
    setOpenSKUModal(true);
  };

  const getLeftValue = () => {
    return currentVotingTeam === 'Steering Reco'
      ? '-225px'
      : currentVotingTeam === 'Completion Team'
        ? '-24px'
        : currentVotingTeam === 'Decision Team'
          ? '-24px'
          : '-295px';
  };

  const currentPageData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  useEffect(() => {
    if (tableName == 'Review Items Table') {
      dispatch(setReviewItemsTableData(originalData));
    }
    setData(originalData);
  }, [originalData]);

  useEffect(() => {
    setPage(0);
  }, [filterPagination]);

  useEffect(() => {
    const newFilterOptions = generateFilterOptions(
      originalColumns,
      originalData,
      isRecommendationTable
    );
    setFilterOptions(newFilterOptions);
    if (isTableLoading) setDrawerOpen(true);
  }, [originalColumns, originalData, isTableLoading]);

  useEffect(() => {
    function updateTableContainerHeight() {
      if (tableContainerRef.current && tableFiltersRef.current) {
        tableContainerRef.current.style.maxHeight = `calc(100vh - ${tableFiltersRef.current.clientHeight}px - 175px)`;
      }
    }

    // Run once on mount
    updateTableContainerHeight();

    window.addEventListener('resize', updateTableContainerHeight);

    return () => {
      window.removeEventListener('resize', updateTableContainerHeight);
    };
  }, []);
  const findColumnMinMax = (data, column) => {
    if (!data.length) return { min: 0, max: 0 };

    const { min, max } = data.reduce(
      (acc, item) => {
        const val = item?.[column] || null;
        return {
          min: Math.min(acc.min, val),
          max: Math.max(acc.max, val)
        };
      },
      { min: Infinity, max: -Infinity }
    );

    return { min, max };
  };
  const handleNumberRange = (column = '', label = '') => {
    let rangeFilters = { ...selectedRangeFilters };
    let updatedData = [...data];
    if (FINANCIAL_KPI_COLUMNS.includes(column)) {
      const { min, max } = findColumnMinMax(originalData, column);
      // eslint-disable-next-line no-prototype-builtins
      if (!rangeFilters?.hasOwnProperty(column)) {
        rangeFilters[column] = { range: [min, max], selectedRange: [min, max] };
        setSelectedRangeFilters(rangeFilters);
        setData(updatedData);
      }
      setSelectedColumnRangeFilter(column);
      setNumberRangePopupOpen(true);
      setSelectedColumnLabel(label);
    }
  };
  const setMinMaxRange = (column, range) => {
    const colRange = selectedRangeFilters[column]?.range || {};
    if (range[0] === colRange[0] && range[1] === colRange[1]) {
      const updatedRangeFilters = { ...selectedRangeFilters };
      delete updatedRangeFilters[column];
      setSelectedRangeFilters(updatedRangeFilters);
    } else {
      const updatedRangeFilters = {
        ...selectedRangeFilters,
        [column]: {
          ...selectedRangeFilters[column],
          selectedRange: range
        }
      };
      setSelectedRangeFilters(updatedRangeFilters);
    }

    setNumberRangePopupOpen(false);
  };
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        sx={{
          padding: '8px 16px 8px 16px',
          position: 'sticky',
          top: 60,
          zIndex: 4,
          backgroundColor: '#fbfcfe'
        }}
        ref={tableFiltersRef}>
        <Stack direction="row" alignItems="center" gap="16px" flexWrap="wrap">
          <Typography variant="h6">{isRecommendationTable ? 'Data Table' : tableName}</Typography>
          {isColumnVisibleFilter && (
            <Box sx={{ width: '200px' }}>
              <CustomAutoComplete
                options={
                  isRecommendationTable
                    ? originalColumns
                        ?.filter((item, index) => !item?.sticky)
                        ?.map((li) => li?.label)
                    : originalColumns.map(({ label }) => label)
                }
                selectedOptions={
                  isRecommendationTable
                    ? columns?.filter((item, index) => !item?.sticky)?.map(({ label }) => label)
                    : columns.map(({ label }) => label)
                }
                onOptionsChange={handleColumnVisibilityChange}
              />
            </Box>
          )}
          {/* SKU Master */}
          {tableName == 'SKU Master' ? (
            <>
              <Typography variant="h7">Category:</Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={handleCategoryMappingChange}
                      name="attributes"
                      checked={attributeChecked}
                    />
                  }
                  label="Attributes"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={handleCategoryMappingChange}
                      name="finance"
                      checked={financeChecked}
                    />
                  }
                  label="Finance"
                />
              </FormGroup>
            </>
          ) : (
            <></>
          )}
          {DMCRegionPopup && (
            <DMCPopupTable
              data={DMCRegionCol}
              setDMCPopupOpen={(val) => {
                setDMCPopupOpen(val);
              }}
            />
          )}
          {numberRangePopupOpen && (
            <NumberRangePopup
              handlePopupClose={() => {
                setNumberRangePopupOpen(false);
              }}
              column={selectedColumnRangeFilter}
              min={selectedRangeFilters?.[selectedColumnRangeFilter]?.range?.[0]}
              max={selectedRangeFilters?.[selectedColumnRangeFilter]?.range?.[1]}
              selectedRange={selectedRangeFilters?.[selectedColumnRangeFilter]?.selectedRange}
              setMinMaxRange={(range) => {
                setMinMaxRange(selectedColumnRangeFilter, range);
              }}
              label={selectedColumnLabel}
            />
          )}{' '}
          {tableName == 'Master Data Table' ? (
            <>
              <Typography variant="h7">Category:</Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={handleCategoryMappingChange}
                      name="attributes"
                      checked={attributeChecked}
                    />
                  }
                  label="Attributes"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={handleCategoryMappingChange}
                      name="finance"
                      checked={financeChecked}
                    />
                  }
                  label="Finance"
                />
              </FormGroup>
            </>
          ) : (
            <></>
          )}
          {tableName === 'Team' ? (
            isLeadsList ? (
              <Box
                sx={{
                  background: 'white',
                  p: '5px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  maxWidth: '50rem'
                }}>
                {isLeadsList.length
                  ? isLeadsList.map((lead, i) => {
                      if (i < 2) {
                        return (
                          <>
                            <span
                              key={`${i + 1}`}
                              style={{
                                fontSize: '14px',
                                marginLeft: '10px'
                              }}>
                              {lead.userName.split(' ')[0]}
                            </span>
                            <span key={`${i + 1}`} style={{ fontSize: '12px', color: '#616161' }}>
                              {' - ' + lead.userEmail}
                            </span>
                          </>
                        );
                      }
                    })
                  : ' '}
                {isLeadsList.length > 2 ? (
                  <span
                    style={{
                      cursor: 'pointer',
                      fontSize: '12px',
                      marginLeft: '10px'
                    }}
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}>
                    {'...'}
                  </span>
                ) : (
                  ''
                )}
                <Popover
                  id="mouse-over-popover"
                  sx={{ pointerEvents: 'none' }}
                  open={open}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                  onClose={handlePopoverClose}
                  disableRestoreFocus>
                  <Box sx={{ background: 'white', p: '5px' }}>
                    {isLeadsList.map((lead, i) => {
                      if (i > 1) {
                        return (
                          <>
                            <span
                              key={`${i + 1}`}
                              style={{
                                fontSize: '14px',
                                marginLeft: '10px'
                              }}>
                              {lead.userName.split(' ')[0]}
                            </span>
                            <span key={`${i + 1}`} style={{ fontSize: '12px', color: '#616161' }}>
                              {' - ' + lead.userEmail}
                            </span>
                            <br />
                          </>
                        );
                      }
                    })}
                  </Box>
                </Popover>
              </Box>
            ) : (
              ''
            )
          ) : null}
        </Stack>
        <Stack direction="row" alignItems="center" gap="16px" flexWrap="wrap">
          {isAddRow && tableName == 'Cycles List' && (
            <>
              <Button
                variant="outlined"
                onClick={handleCycleDiscardChanges}
                disabled={!isCycleEdit}
                sx={{ fontSize: '12px' }}>
                Discard
              </Button>
              <Button
                variant="contained"
                data-testid="save-btn"
                onClick={
                  tableName == 'Review Items Table'
                    ? handleCheckItemSaveChanges
                    : tableName == 'Cycles List'
                      ? handleCycleCreate
                      : handleSaveChanges
                }
                disabled={
                  !(changedData.length || Object.keys(newRowData).length || editCellData.length) ||
                  showCycleSave
                }
                sx={{ fontSize: '12px' }}>
                Save
              </Button>
            </>
          )}
          {isRefresh && (
            <Button>
              <img src={refreshIcon} alt="refreshIcon" />
            </Button>
          )}
          {isSearch && (
            <StyledTextField
              id={`${tableName}-search`}
              placeholder="Search..."
              variant="standard"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          )}
          {isEditable && (
            <>
              <Button
                variant="outlined"
                onClick={
                  tableName == 'Review Items Table'
                    ? handleCheckItemDiscardChanges
                    : handleDiscardChanges
                }
                disabled={
                  !(changedData.length || Object.keys(newRowData).length || editCellData.length)
                }>
                Discard Changes
              </Button>
              <Button
                variant="contained"
                onClick={
                  tableName == 'Review Items Table' ? handleCheckItemSaveChanges : handleSaveChanges
                }
                disabled={
                  !(changedData.length || Object.keys(newRowData).length || editCellData.length)
                }>
                Save Changes
              </Button>
            </>
          )}
          {[
            'Recommendation Table',
            'Completion Table',
            'Decision Table',
            'Master Data Table',
            'SKU Master'
          ].includes(tableName) && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleResetFilters}
              sx={{ fontSize: '12px',color: '#0078A7',borderColor: '#0078A7' }}>
              Clear table filters
            </Button>
          )}
          {downloadExcel && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadOutlined />}
              onClick={() => handleDownloadData()}
              disabled={isUploadDownloadDisabled}>
              {tableName === 'Recommendation Table' ||
              tableName === 'Completion Table' ||
              tableName === 'Decision Table'
                ? 'Export table'
                : 'Download'}
            </Button>
          )}
          {(isBulkUpload && !userDetails?.roles.includes(GENERAL_USER)) ||
          tableName === 'Recommendation Table' ||
          tableName === 'Completion Table' ||
          tableName === 'Decision Table' ? (
            <Button
              id="bulk-upload"
              variant="contained"
              color="primary"
              startIcon={<UploadOutlined />}
              disabled={
                isUploadDownloadDisabled
              }
              onClick={onBulkUploadModalOpen}>
              {tableName === 'Recommendation Table' ||
              tableName === 'Completion Table' ||
              tableName === 'Decision Table'
                ? 'Import Excel'
                : 'Bulk Upload'}
            </Button>
          ) : null}
          {isGlobalFilter && (
            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: '5px', // Make the button rectangular
                padding: '3px', // Top and bottom padding: 2px, left and right padding: 0px
                margin: '0px', // Optional: Remove margin if needed
                minWidth: 'unset' // Optional: Ensure button width is not constrained by minWidth
              }}
              onClick={() => setIsAdvanceFilterModalOpen(true)}>
              <FilterListIcon />
            </Button>
          )}
          {showTableText && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="small" sx={{ color: '#616161' }}>
                {showTableText}
              </Typography>
              {currentVotingTeam === 'Functional Feedback' && (
                <Typography variant="small" sx={{ color: '#616161' }}>
                  {generateKPIHighlightText(KPI_HIGHLIGHTER)}
                </Typography>
              )}
            </div>
          )}
        </Stack>
      </Stack>
      <StyledTableContainer ref={tableContainerRef}>
        <Table size={density} sx={{ minHeight: '400px' }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fbfcfe' }}>
            <StyledTableRow sx={{ borderBottom: 'none !important' }}>
              {isCollapseButton && (
                <StyledTableHeadCell
                  sticky
                  left={0}
                  isCheckbox
                  isRecommendationTable={isRecommendationTable}
                />
              )}{' '}
              {isRowChecked && !showCheckbox && (
                <StyledTableHeadCell
                  sticky
                  left={0}
                  isCheckbox
                  isRecommendationTable={isRecommendationTable}
                  width={'10px'}>
                  <Checkbox
                    data-testid="header-checkbox"
                    checked={checkedRows.length === currentPageData.length}
                    size="small"
                    sx={{ padding: 'initial' }}
                    onChange={(e) => handleAllRowsChecked(e)}></Checkbox>
                </StyledTableHeadCell>
              )}
              {columns.map((column) => (
                <>
                  {(column.id === 'agreeDisagreeStatus' &&
                    ['Decision Table', 'Completion Table'].includes(tableName) === false) ||
                  (tableName === 'Decision Table' && column.id === 'decision' && !showCheckbox) ||
                  (tableName === 'Completion Table' && column.id === 'decision' && !showCheckbox) ||
                  (column.id === 'completionStatus' && !showCheckbox) ? (
                    <TableCell
                      key={column.id}
                      style={{
                        position: 'sticky',
                        zIndex: 3,
                        backgroundColor: '#EFF5FC',
                        right: column.right,
                        width: column.width,
                        minWidth: column.width,
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#616161'
                      }}>
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: getLeftValue()
                        }}>
                        <IconButton
                          size="small"
                          style={{
                            padding: '0px',
                            borderLeft: '1px solid #005CBC',
                            borderRadius: '0px'
                          }}
                          onClick={() => drawerCollapse()}>
                          {drawerOpen ? (
                            <ChevronRightIcon
                              style={{
                                background: '#EFF5FC',
                                borderRadius: '0px',
                                color: '#005CBC',
                                zIndex: 3
                              }}
                            />
                          ) : (
                            <ChevronLeftIcon
                              style={{
                                background: '#EFF5FC',
                                borderRadius: '0px',
                                color: '#005CBC',
                                zIndex: 3
                              }}
                            />
                          )}
                        </IconButton>
                      </div>
                      {column.label}
                    </TableCell>
                  ) : (
                    <StyledTableHeadCell
                      key={column.id}
                      align={column.align || 'left'}
                      sticky={column.sticky}
                      left={column.left}
                      right={column.right}
                      width={column.width}
                      showBorder={column.showBorder}
                      isRecommendationTable={isRecommendationTable}
                      customerFilter={customerFilter}
                      customerKey={column.id}>
                      <TableSortLabel
                        active={sortConfig?.key === column.id}
                        direction={sortConfig?.key === null ? 'asc' : sortConfig?.direction}
                        onClick={() => handleRequestSort(column.id)}>
                        {column.label}
                      </TableSortLabel>
                    </StyledTableHeadCell>
                  )}
                </>
              ))}
              {!drawerOpen && (
                <TableCell
                  style={{
                    position: 'sticky',
                    width: columns?.length < 6 ? '' : '30px',
                    right: 0,
                    zIndex: 3,
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: columns?.length < 6 ? 'end' : 'center',
                    marginRight: '-10px'
                  }}>
                  <IconButton
                    size="small"
                    style={{
                      padding: '0px',
                      borderLeft: '1px solid #005CBC',
                      borderRadius: '0px',
                      zIndex: 3
                    }}
                    onClick={() => drawerCollapse()}>
                    <ChevronLeftIcon
                      style={{
                        background: '#EFF5FC',
                        borderRadius: '0px',
                        color: '#005CBC',
                        zIndex: 3
                      }}
                    />
                  </IconButton>
                </TableCell>
              )}
            </StyledTableRow>
            {isColumnFilter && (
              <StyledTableRow>
                {isCollapseButton && (
                  <StyledTableHeadCell sticky left={0} isRecommendationTable isCheckbox />
                )}

                {/* for disabling checkbox  in header row for filters option*/}
                {isRowChecked && !showCheckbox && (
                  <StyledTableHeadCell
                    sticky
                    left={0}
                    isCheckbox
                    isRecommendationTable={isRecommendationTable}
                  />
                )}
                {columns.map((column) => (
                  <StyledTableHeadCell
                    key={column.id}
                    align={column.align || 'left'}
                    sticky={column.sticky}
                    left={column.left}
                    right={column.right}
                    showBorder={column.showBorder}
                    isRecommendationTable={isRecommendationTable}>
                    {column.id != 'activeStatus' &&
                      column.id != 'moreActions' &&
                      column.id !== 'functional team votes' &&
                      column.id !== 'REGIONAL TEAM VOTES' &&
                      column.id !== 'INPUTS FROM REGIONAL TEAMS' &&
                      column.id !== 'collated comments' &&
                      column.id !== 'functional stakeholders collated comments' &&
                      column.id !== 'functional stakeholders collated comments' &&
                      column.id !== 'functional team comments' &&
                      column.id != 'startCycle' && (
                        <Stack
                          sx={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                          }}>
                          <CustomAutoComplete
                            options={filterOptions[column.id] ?? []}
                            selectedOptions={selectedFilters[column.id] ?? []}
                            onOptionsChange={(selectedValues) =>
                              handleFilterChange(column.id, selectedValues)
                            }
                          />
                          <FilterListIcon
                            sx={{
                              fontSize: '18px',
                              cursor: 'pointer',
                              color:
                                selectedRangeFilters[column.id] &&
                                !selectedRangeFilters[column.id]?.range?.every(
                                  (value, index) =>
                                    value ===
                                    selectedRangeFilters[column.id]?.selectedRange?.[index]
                                )
                                  ? '#0078A7'
                                  : 'inherit'
                            }}
                            onClick={() => {
                              handleNumberRange(column.id, column?.label);
                            }}
                          />
                        </Stack>
                      )}
                  </StyledTableHeadCell>
                ))}
              </StyledTableRow>
            )}
          </TableHead>

          <TableBody>
            {isAddRow && isNewRowVisible && (
              <StyledTableRow sx={{ backgroundColor: '#DFEAF9' }}>
                {isCollapseButton && <StyledTableCell />}
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sticky={column.sticky}
                    left={column.left}
                    right={column.right}
                    width={column.width}
                    showBorder={column.showBorder}>
                    {column.addCell && (
                      <>
                        {column.type === 'text' &&
                          // Check if the column id is either 'startYearPeriod' or 'endYearPeriod'
                          (column.id === 'startYearPeriod' || column.id === 'endYearPeriod' ? (
                            <FormControl fullWidth variant="outlined" size="small">
                              <InputLabel>YYYYPP</InputLabel>
                              <Select
                                size="small"
                                labelId={`select-label-${column.id}`}
                                id={`select-${column.id}`}
                                name={column.id}
                                value={newRowData[column.id] ?? ''}
                                label={'YYYYPP'}
                                onChange={handleNewRowChange} // Add onChange event
                                sx={{ width: '8rem' }}>
                                {yearMonthData.map((date, idx) => (
                                  <MenuItem
                                    key={`${idx + 1}`}
                                    value={date}
                                    disabled={
                                      column.id === 'startYearPeriod'
                                        ? newRowData['endYearPeriod'] &&
                                          date >= newRowData['endYearPeriod'] // Disable invalid start dates
                                        : newRowData['startYearPeriod'] &&
                                          date <= newRowData['startYearPeriod'] // Disable invalid end dates
                                    }>
                                    {date}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : column.id === 'cycleName' || column.id === 'businessUnitName' ? (
                            // Render TextField for any other text column
                            <TextField
                              placeholder="User Input"
                              size="small"
                              name={column.id}
                              value={newRowData[column.id] ?? ''}
                              onChange={handleNewRowChange}
                            />
                          ) : (
                            ''
                          ))}
                        {column.type === 'checkbox' && (
                          <Checkbox
                            size="small"
                            sx={{ padding: 'initial' }}
                            name={column.id}
                            checked={newRowData[column.id] ?? false}
                            onChange={handleNewRowChange}
                          />
                        )}
                        {column.type === 'delete' ||
                          (column.type == 'cycleActions' && (
                            <StyledIconButton aria-label="delete" onClick={handleDeleteNewRow}>
                              <DeleteOutline />
                            </StyledIconButton>
                          ))}
                      </>
                    )}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            )}
            {isTableLoading &&
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <StyledTableRow key={`${index + 1}`}>
                  <StyledTableCell />
                  {columns.map((column) => (
                    <StyledTableCell key={column.id} sticky={column.sticky}>
                      <Skeleton animation="wave" />
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))}
            {!isTableLoading && (data.length === 0 || currentPageData.length === 0) && (
              <StyledTableRow>
                <StyledTableCell
                  colSpan={columns.length + (isCollapseButton ? 1 : 0)}
                  sx={{ textAlign: 'center' }}>
                  <MobiledataOffIcon sx={{ fontSize: '40px', color: '#3A3A3A' }} />
                  <Typography variant="h6" sx={{ color: '#3A3A3A' }}>
                    No Data Found
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}

            {/* Looping into data , for showing in table */}
            {!isTableLoading &&
              currentPageData?.map((row, index) => (
                <React.Fragment key={row.id || index}>
                  <StyledTableRow
                    isExpanded={expandedRow === index}
                    isRowSelected={row.activeStatus}>
                    {isCollapseButton && (
                      <StyledTableCell sticky left={0} isCheckbox>
                        <IconButton
                          aria-label="expand row"
                          data-testid="arrow-test"
                          size="small"
                          onClick={() => handleExpandClick(row, index)}
                          sx={{ padding: 0 }}>
                          {expandedRow === index ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </StyledTableCell>
                    )}

                    {/* Adding checkbox for each row for voting screen*/}
                    {isRowChecked && !showCheckbox && (
                      <StyledTableCell sticky left={0} isCheckbox width={'10px'}>
                        <Checkbox
                          data-testid={`checkbox-${index + 1}`}
                          checked={checkedRows?.includes(row.CycleSKUKey)}
                          size="small"
                          sx={{ padding: 'initial' }}
                          onChange={(e) => handleCheckboxChange(e, row)}
                        />
                      </StyledTableCell>
                    )}

                    {columns &&
                      columns.length > 0 &&
                      [...columns].map((column) => (
                        <React.Fragment key={column.id}>
                          <StyledTableCell
                            key={column.id}
                            align={column.align || 'left'}
                            sticky={column.sticky}
                            left={column.left}
                            right={column.right}
                            width={column.width}
                            showBorder={column.showBorder}
                            isRecommendationTable={isRecommendationTable}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                              }}>
                              <div>
                                {tableName === 'Review Items Table' &&
                                !userDetails?.roles.includes('GENERAL_USER') ? (
                                  <ReviewItemsCellContent
                                    columns={[...columns]}
                                    column={{ ...column }}
                                    row={{ ...row }}
                                    originalData={originalData}
                                    isEditable={isEditable}
                                    editCellData={editCellData}
                                    onEditCell={(rowID, columnId, content, rowObj) =>
                                      handleEditCell(rowID, columnId, content, rowObj)
                                    }
                                    tableName={tableName}
                                    data={data}
                                    renderFlag={renderFlag}
                                  />
                                ) : isRecommendationTable &&
                                  column?.sticky &&
                                  !column?.showBorder ? (
                                  <DrawerColumns column={column} row={row} tableName={tableName} />
                                ) : (
                                  <CellContent
                                    columns={[...columns]}
                                    column={{ ...column }}
                                    row={{ ...row }}
                                    originalData={originalData}
                                    isCycleEdit={isCycleEdit}
                                    setIsCycleEdit={(isCycleEdit) => setIsCycleEdit(isCycleEdit)}
                                    editingRowId={editingRowId}
                                    setEditingRowId={(editingRowId) =>
                                      setEditingRowId(editingRowId)
                                    }
                                    setShowCycleSave={(showCycleSave) =>
                                      setShowCycleSave(showCycleSave)
                                    }
                                    showCycleSave={showCycleSave}
                                    tableName={tableName}
                                    getCycleHistory={getCycleHistory}
                                    yearMonthData={yearMonthData}
                                    onValueChange={(value) => handleValueChange(value, row, column)}
                                    onRadioChange={(value) => handleRadioChange(value, row, column)}
                                    onDrawerChange={(value) =>
                                      handleDrawerChange(value, row, column, index)
                                    }
                                    handleLogs={(data) => handleLogsData(data)}
                                    isSteeringRecoCompleted={isSteeringRecoCompleted}
                                    handleGoalsModal={(rowdata) => handleGoalsModal(rowdata)}
                                    showCycleDrawer={(rowdata) => showCycleDrawer(rowdata)}
                                    handleModalAction={handleModalAction}
                                    isRecommendationTable={isRecommendationTable}
                                    setDMCPopup={(val) => {
                                      setDMCPopupOpen(val);
                                    }}
                                    setDMCRegionCol={(data) => {
                                      setDMCRegionCol(data);
                                    }}
                                  />
                                )}
                              </div>

                              {column.id === 'sku name' && isRecommendationTable && (
                                <StyledIconButton
                                  aria-label="info"
                                  size="small"
                                  sx={{ padding: 0 }}
                                  onClick={() => handleInfoClick(column, row, index)}>
                                  <TrendSymbol style={{ width: '32px', height: '24px' }} />
                                </StyledIconButton>
                              )}
                            </div>
                          </StyledTableCell>
                        </React.Fragment>
                      ))}
                  </StyledTableRow>
                  {!isRecommendationTable && (
                    <StyledTableRow isExpanded={expandedRow === index}>
                      <StyledTableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={columns.length + 1}>
                        <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                          <Box margin={1}>
                            {tableName == 'Master Data Table' || tableName == 'Cycles List' ? (
                              <DetailTable
                                childData={rowChildData.length > 0 ? rowChildData : []}
                              />
                            ) : (
                              <DetailTable childData={row?.child ?? []} />
                            )}
                          </Box>
                        </Collapse>
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </React.Fragment>
              ))}
            {!isTableLoading && (
              <div style={{ height: density === 'small' ? '20px' : '56px' }}></div>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <Box display="flex" justifyContent="center" padding={2}>
        {originalData && originalData.length > 0 && (
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            inputProps={{ 'aria-label': 'rows per page' }}
            style={{
              width: '85px',
              height: '34px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '400',
              padding: '6px 6px',
              marginRight: '10px',
              color: '#005CBC',
              backgroundColor: '#EFF5FC',
              border: '1px solid #D7E5F8',
              '&:hover': {
                border: '1px solid #D7E5F8'
              }
            }}>
            <MenuItem key={5} value={5}>
              5
            </MenuItem>
            <MenuItem key={10} value={10}>
              10
            </MenuItem>
            <MenuItem key={20} value={20}>
              20
            </MenuItem>
            <MenuItem key={30} value={30}>
              30
            </MenuItem>
            <MenuItem key={40} value={40}>
              40
            </MenuItem>
            <MenuItem key={50} value={50}>
              50
            </MenuItem>
            <MenuItem key={60} value={60}>
              60
            </MenuItem>
            <MenuItem key={70} value={70}>
              70
            </MenuItem>
            <MenuItem key={80} value={80}>
              80
            </MenuItem>
            <MenuItem key={90} value={90}>
              90
            </MenuItem>
            <MenuItem key={100} value={100}>
              100
            </MenuItem>
          </Select>
        )}

        <Pagination
          pageCount={Math.ceil(sortedData.length / rowsPerPage)}
          currentPage={page + 1}
          onUpdate={(newpage) => handleChangePage(newpage)}
          variant="number"
        />
      </Box>

      <AdvancedFilter
        columns={originalColumns}
        open={isAdvanceFilterModalOpen}
        onClose={() => setIsAdvanceFilterModalOpen(false)}
        data={filteredData(selectedFilters)}
        selectedFilters={selectedFilters}
        onFilterChange={(selectedFilters) => handleApplyAdvanceFilter(selectedFilters)}
      />
      {/* comments drawer */}
      {showCommentDrawer && (
        <PopDrawer
          drawerOpen={showCommentDrawer}
          toggleDrawer={(val) => toggleDrawerHandler(val)}
          rowData={rowData}
          rowIndex={rowIndex}
          wholeData={currentPageData}
          commentType={commentType}
        />
      )}
      {openSKUModal && (
        <SKUModal
          open={openSKUModal}
          onClose={() => {
            setOpenSKUModal(false);
          }}
          column={selectedColumn}
          rowData={rowData}
          setRowData={(rowData) => {
            setRowData(rowData);
          }}
          rowIndex={rowIndex}
          setRowIndex={(rowIndex) => {
            setRowIndex(rowIndex);
          }}
          wholeData={currentPageData}
          productAttr={productAttr}
          skuData={skuData}
          currentVotingTeam={currentVotingTeam}
          setSKUData={(skuData) => {
            setSKUData(skuData);
          }}
          onDrawerCommentChange={(value, type) =>
            handleDrawerCommentChange(value, type, rowData, selectedColumn, rowIndex)
          }
        />
      )}
      {showSKUCommentDrawer && (
        <PopCommentDrawer
          drawerOpen={showSKUCommentDrawer}
          toggleDrawer={(val, data) => toggleDrawerCommentHandler(val, data)}
          rowData={rowData}
          rowIndex={rowIndex}
          wholeData={currentPageData}
          commentType={commentType}
          page={'voting'}
        />
      )}
    </Box>
  );
}

export default DynamicTable;
